// Angular
import {
  Component,
  forwardRef,
  ViewChild,
  Injector,
  ChangeDetectionStrategy,
  Injectable,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { ObjectUtils } from "@bfx/primeng/utils";
import { InputMask } from "@bfx/primeng/inputmask";
// BCDFX - Globals

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// Services
// Components
import { ICDBase, ICD_BASE_DEFAULT_HOST_RECORD } from "./icd-base.component";
// Models
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ICDRadioInputModel } from "../../data-idc/controls/icd-radio-input.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ATDLEnumModel } from "../../data-atdl/model/atdl-enum.model";
import { ICDEnumModel } from "../../data-idc/controls/icd-enum.model";
// Enums - ICD Coordination
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
// Enums - ATDL
// Enums
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";
import { EmphasisMixin } from "../../../../shared/base-components/mixins/emphasis";
import { FieldStateMixin } from "../../../../shared/base-components/mixins/field-state";
import { InputtedMixin } from "../../../../shared/base-components/mixins/inputted";
import { InputLabelStateMixin } from "../../../../shared/base-components/mixins/label-state";
import { PlaceholderMixin } from "../../../../shared/base-components/mixins/placeholder";
import { SizeMixin } from "../../../../shared/base-components/mixins/size";

@Injectable()
export class ICDCRadioButton extends ICDBase<ICDRadioInputModel> {
  /**********************************************************************/
  /* DATA */
  public config: any = {};

  public subData: any = {
    dataset: [],
    optionValue: null, // internal control model -> string
    initValue: null,
  };

  /************************************/
  /* Element Ref */
  @ViewChild("inputField", { static: true }) inputField: InputMask;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(injector: Injector) {
    super(injector);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZE */

  protected initialize(): void {
    super.initialize();
    this.label = "Radio";
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL API  */

  protected initializeICDSpecificModelProperties(): boolean {
    this.cd.markForCheck();
    let error: boolean = false;
    if (this.model instanceof ATDLInterfaceControlModel) {
      // DATASET
      if (
        this.model.dataset instanceof Array &&
        (this.model.dataset.length === 2 || this.model.dataset.length === 1)
      ) {
        for (let dataItem of this.model.dataset) {
          if (dataItem instanceof ATDLEnumModel) {
            let uiRep = dataItem.uiRep;
            let tooltip = "";
            if (typeof uiRep === "string" && uiRep.length > 11) {
              uiRep = uiRep.substring(0, 9) + "..";
              tooltip = dataItem.uiRep;
            }
            this.data.dataset.push(
              new ICDEnumModel(dataItem.enumId, uiRep, this.icdId + dataItem.enumId, tooltip),
            );
          }
        }
      } else {
        error = true;
        logger.out.error(
          `ERROR: ${this.constructor.name}.initializeICDSpecificModelProperties(): Invalid dataset!`,
        );
      }

      // INIT VALUE
      if (!error) {
        if (typeof this.model.initValue === "string") {
          this.data.initValue = this.model.initValue;
          this.setAndUpdateValue(this.model.initValue);
        } else if (typeof this.model.defaultValue === "string") {
          this.data.defaultValue = this.model.initValue;
          this.setAndUpdateValue(this.model.defaultValue);
        } else {
          this.setAndUpdateValue(this.data.dataset[0].value);
        }
        this.validate();
      }
    } else {
      error = true;
    }
    return error;
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */

  writeValue(value: any): void {
    this.data.value = value;
    this.model.value = this.data.value;
  }

  protected handleConstraintUpdate(data: any): void {
    this.handleDynResponses(this.processExternalStateFlowDependency(data, new Map()));
  }

  /**********************************************************************/
  /* EVENTS */
  public onFocus(event: any): void {
    this.inputFocused = true;
    this.dirty = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.data.errorMsg = "";
  }

  public onBlur(event: any): void {
    this.inputFocused = false;
    this.executeBlur();
  }

  public focusInput() {}

  /**********************************************************************/
  /* EXTERNAL API */

  private executeBlur(): void {
    this.updateValue();
    this.validate();
  }

  public setDefault(): void {
    //this.setAndUpdateValue(this.data.defaultValue);
  }

  protected setAndUpdateValue(value: any): void {
    if (typeof value === "string" || value instanceof ICDRadioInputModel) {
      this.set(value);
      this.updateValue();
    } else {
      logger.out.error(`ERROR: ${this.constructor.name}.setAndUpdateValue(): Invalid value`);
    }
  }

  public updateValue(): void {
    const newValue = this.compose();
    this.updateModel(newValue);
  }

  /**
   * Apply value change to local data, update the value in the control model, and signal change
   * @param value
   */
  protected updateModel(value: ICDRadioInputModel): void {
    if (value instanceof ICDRadioInputModel) {
      if (this.modelValueChanged(value)) {
        this.data.value = value;
        this.model.value = value;
        this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
        this.cd.markForCheck();
      }
    } else {
      logger.out.error("writeValue NOT ICDRadioInputModel");
    }
  }

  /**
   * Has the model value changed?
   * @param value
   * @returns
   */
  protected modelValueChanged(value: ICDRadioInputModel) {
    const oldValue: ICDRadioInputModel = this.data.value;
    if (!oldValue || !value) {
      return true;
    }
    return (
      value.value !== oldValue.value ||
      value.nulledByDependency !== oldValue.nulledByDependency ||
      value.disabled !== oldValue.disabled
    );
  }

  public load(value: ICDRadioInputModel, propagate?: boolean): void {
    let dynResponses = new Map();
    if (value instanceof ICDRadioInputModel || value === null) {
      this.setAndUpdateValue(value);
      this.validate();
      dynResponses = this.processAuxStateFlow(dynResponses);

      dynResponses.set(ICDNResponseCommand.LOAD_COMPLETE, null);

      if (typeof propagate === "boolean" && propagate) {
        dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
      }
    }
    this.handleDynResponses(dynResponses);
  }

  /**
   * The set() method is used in order to assign value to the internal(displayed) control model ('this.data.optionValue') as well as
   * the auxCheckbox when proper type (ICDTextFieldModel) is provided.
   */
  public set(value: ICDRadioInputModel | string): void {
    if (value instanceof ICDRadioInputModel || typeof value === "string" || value == null) {
      this.dirty = true;
      if (value instanceof ICDRadioInputModel) {
        this.data.optionValue = value.value;
      } else if (typeof value === "string") {
        this.data.optionValue = value;
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid value type`);
      }
      this.cd.markForCheck();
    }
  }

  /**
   * The compose() method is used in order to generate an outgoing model for the control (in this case ICDTextFieldModel)
   * which will be assigned to the main model 'this.data.value' and propagated to the panel.
   */
  private compose(): ICDRadioInputModel {
    let model = new ICDRadioInputModel();
    let auxModel = new ICDAuxCheckboxModel();
    auxModel.enabled = false;
    auxModel.mode = ICDNAuxMode.REQUIRED;
    auxModel.value = false;
    model.value =
      typeof this.data.optionValue === "string" && this.data.optionValue.length > 0
        ? this.data.optionValue
        : null;
    model.nulledByDependency = this.data?.value?.nulledByDependency ?? false;
    model.disabled = this.disabled || this.data.disabled || !this.visible;
    model.auxModel = auxModel;
    return model;
  }

  public validateIC(): boolean {
    this.cd.markForCheck();

    if (
      this.data.value instanceof ICDRadioInputModel &&
      typeof this.data.value.value === "string" &&
      this.data.dataset.find((item) => item.value === this.data.value.value) != null
    ) {
      this.data.state = ICNInputControlState.VALID;
      this.data.errorMsg = "";
      return true;
    } else if (
      this.required &&
      (!(this.data.value instanceof ICDRadioInputModel) ||
        typeof this.data.value.value !== "string")
    ) {
      this.data.state = ICNInputControlState.INVALID;
      this.data.errorMsg = `${this.label} must not be empty`;
      return false;
    } else if (
      !this.required &&
      (this.disabled ||
        this.data.disabled ||
        this.data.value == null ||
        this.data.value.value == null)
    ) {
      this.data.state = ICNInputControlState.VALID;
      this.data.errorMsg = "";
      return true;
    } else {
      this.data.state = ICNInputControlState.INVALID;
      this.data.errorMsg = `${this.label} value not in dataset`;
      return false;
    }
  }

  public resetIC(): void {
    this.cd.markForCheck();
    this.data.dataset = [];
    this.data.optionValue = null;
    this.data.initValue = null;
  }

  /**********************************************************************/
  /* RADIO */

  public clickEvent(): void {
    this.executeBlur();
  }
}

const Mixins = EmphasisMixin(
  SizeMixin(
    FieldStateMixin(InputLabelStateMixin(InputtedMixin(PlaceholderMixin(ICDCRadioButton)))),
  ),
);

@Component({
  selector: "icd-radio-button",
  host: {
    ...ICD_BASE_DEFAULT_HOST_RECORD,
    "[class.icd__radio-button__container]": "true",
  },
  templateUrl: "./html/icd-radio-button.html",
  providers: [ObjectUtils],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [BfxInputDirective],
  standalone: false,
})
export class ICDCRadioButtonComponent extends Mixins {
  public inputDirective: BfxInputDirective;

  constructor(injector: Injector) {
    super(injector);
    this.inputDirective = this.injector.get(BfxInputDirective);
    this.size = "sm";
    this.emphasis = "bold";
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }
}
